import { useQuery } from "@tanstack/react-query";
import { fetchData } from "../functions/utils";
import { createSocialMediaArray, createNavItemsArray } from "../functions/utils";
import {
  configurationGROQ,
  homePageGROQ,
  pageContentGROQ,
  searchResultsGROQ,
} from "../functions/GROQ";

/**
 * Cache time constants (in milliseconds)
 */
const CACHE_TIMES = {
  configuration: 1000 * 60 * 30, // 30 minutes - site settings rarely change
  pageContent: 1000 * 60 * 10,   // 10 minutes - page content
  products: 1000 * 60 * 5,        // 5 minutes - products (inventory may change)
  search: 1000 * 60 * 2,          // 2 minutes - search results
};

/**
 * Hook to fetch site configuration (settings, navigation, all pages).
 * This data is cached for 30 minutes as it rarely changes.
 * @returns {Object} React Query result with configuration data
 */
export const useConfiguration = () => {
  return useQuery({
    queryKey: ["configuration"],
    queryFn: async () => {
      const configuration = await fetchData(configurationGROQ);
      
      if (!configuration) {
        throw new Error("Failed to fetch configuration");
      }

      const socialMediaFromSettings = configuration.settings.socialMedia;
      const socialMediaArray = createSocialMediaArray(socialMediaFromSettings);
      const navItemsArray = await createNavItemsArray(configuration.navigation.menu);
      const navigationArray = navItemsArray.concat(socialMediaArray);

      return {
        navigation: navigationArray,
        configuration: configuration.settings,
        allPages: configuration.allPages,
      };
    },
    staleTime: CACHE_TIMES.configuration,
    gcTime: CACHE_TIMES.configuration * 2, // Keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on tab focus
  });
};

/**
 * Hook to fetch home page content.
 * @returns {Object} React Query result with home page data
 */
export const useHomePageContent = () => {
  return useQuery({
    queryKey: ["homePage"],
    queryFn: async () => {
      const content = await fetchData(homePageGROQ);
      return content;
    },
    staleTime: CACHE_TIMES.pageContent,
    gcTime: CACHE_TIMES.pageContent * 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch product page content.
 * @param {string} slug - Product slug
 * @returns {Object} React Query result with product data
 */
export const useProductContent = (slug) => {
  return useQuery({
    queryKey: ["product", slug],
    queryFn: async () => {
      const content = await fetchData(pageContentGROQ("product", slug));
      if (content) {
        content.id = content._id;
      }
      return content;
    },
    enabled: !!slug, // Only fetch if slug is provided
    staleTime: CACHE_TIMES.products,
    gcTime: CACHE_TIMES.products * 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch store page content (store settings, all products, categories).
 * @returns {Object} React Query result with store data
 */
export const useStoreContent = () => {
  return useQuery({
    queryKey: ["store"],
    queryFn: async () => {
      const content = await fetchData(pageContentGROQ("store"));
      return content;
    },
    staleTime: CACHE_TIMES.products,
    gcTime: CACHE_TIMES.products * 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch blog page content.
 * @returns {Object} React Query result with blog data
 */
export const useBlogContent = () => {
  return useQuery({
    queryKey: ["blog"],
    queryFn: async () => {
      const content = await fetchData(pageContentGROQ("blog"));
      return content;
    },
    staleTime: CACHE_TIMES.pageContent,
    gcTime: CACHE_TIMES.pageContent * 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch generic page content (pages, blogposts).
 * @param {string} type - Page type ('page' or 'blogpost')
 * @param {string} slug - Page slug
 * @returns {Object} React Query result with page data
 */
export const usePageContent = (type, slug) => {
  return useQuery({
    queryKey: ["page", type, slug],
    queryFn: async () => {
      const content = await fetchData(pageContentGROQ(type, slug));
      return content;
    },
    enabled: !!slug && !!type,
    staleTime: CACHE_TIMES.pageContent,
    gcTime: CACHE_TIMES.pageContent * 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch category page content.
 * @param {string} slug - Category slug
 * @returns {Object} React Query result with category data
 */
export const useCategoryContent = (slug) => {
  return useQuery({
    queryKey: ["category", slug],
    queryFn: async () => {
      const content = await fetchData(pageContentGROQ("category", slug));
      return content;
    },
    enabled: !!slug,
    staleTime: CACHE_TIMES.pageContent,
    gcTime: CACHE_TIMES.pageContent * 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch search results.
 * @param {string} query - Search query
 * @param {string} language - Current language
 * @returns {Object} React Query result with search results
 */
export const useSearchResults = (query, language) => {
  return useQuery({
    queryKey: ["search", query, language],
    queryFn: async () => {
      const results = await fetchData(searchResultsGROQ(query, language));
      return results;
    },
    enabled: !!query && query.length > 0,
    staleTime: CACHE_TIMES.search,
    gcTime: CACHE_TIMES.search * 2,
    refetchOnWindowFocus: false,
  });
};

